import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Package, Trash2, AlertTriangle, Building2 } from 'lucide-react';
import { Plus, History, Layers } from '../components/icons';
import DataTable from '../components/DataTable';
import { consumiblesAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import CrearConsumibleModal from '../components/CrearConsumibleModal';
import AsignarConsumibleModal from '../components/AsignarConsumibleModal';
import DetalleConsumibleModal from '../components/DetalleConsumibleModal';

export default function Consumibles({ embedded = false }) {
    const location = useLocation();
    const { showToast } = useToast();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filtros activos
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedEmpresa, setSelectedEmpresa] = useState('all');
    const [filterLowStock, setFilterLowStock] = useState(false);

    // Estados para modales
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedConsumible, setSelectedConsumible] = useState(null);

    // Sincronizar con parámetros de URL
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const cat = searchParams.get('categoria');
        const emp = searchParams.get('empresa');
        if (cat) setSelectedCategory(cat);
        if (emp) setSelectedEmpresa(emp);
    }, [location.search]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await consumiblesAPI.getAll();
            setData(res.data || []);
        } catch (error) {
            console.error(error);
            showToast('Error', 'No se pudo cargar el inventario', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    const handleDelete = useCallback(async (e, consumible) => {
        e.stopPropagation();
        if (!window.confirm(`¿Estás seguro de eliminar el insumo "${consumible.nombre}"? esta acción no se puede deshacer.`)) {
            return;
        }
        try {
            await consumiblesAPI.delete(consumible.id);
            showToast('Insumo eliminado correctamente', 'success');
            fetchData();
        } catch (error) {
            console.error('Error al eliminar insumo:', error.message);
            showToast(error.response?.data?.message || 'Error al eliminar insumo', 'error');
        }
    }, [showToast, fetchData]);

    // Estadísticas calculadas de insumos
    const stats = useMemo(() => {
        const totalTipos = data.length;
        const totalUnidades = data.reduce((acc, c) => acc + (Number(c.stockActual) || 0), 0);
        const stockBajo = data.filter(c => (Number(c.stockActual) || 0) <= (Number(c.stockMinimo) || 0)).length;

        // Por Categoría
        const categoriasMap = {};
        data.forEach(c => {
            const cat = c.categoria || 'Otros';
            if (!categoriasMap[cat]) {
                categoriasMap[cat] = { count: 0, stock: 0 };
            }
            categoriasMap[cat].count += 1;
            categoriasMap[cat].stock += Number(c.stockActual) || 0;
        });

        // Por Empresa
        const empresasMap = {};
        data.forEach(c => {
            const emp = c.empresa || c.Empresa || 'General';
            if (!empresasMap[emp]) {
                empresasMap[emp] = { count: 0, stock: 0 };
            }
            empresasMap[emp].count += 1;
            empresasMap[emp].stock += Number(c.stockActual) || 0;
        });

        return {
            totalTipos,
            totalUnidades,
            stockBajo,
            categorias: Object.entries(categoriasMap).map(([nombre, val]) => ({ nombre, ...val })),
            empresas: Object.entries(empresasMap).map(([nombre, val]) => ({ nombre, ...val }))
        };
    }, [data]);

    // Datos filtrados
    const filteredData = useMemo(() => {
        return data.filter(item => {
            const matchCategory = selectedCategory === 'all' || item.categoria?.toLowerCase() === selectedCategory.toLowerCase();
            const itemEmpresa = item.empresa || item.Empresa || 'General';
            const matchEmpresa = selectedEmpresa === 'all' || itemEmpresa.toLowerCase() === selectedEmpresa.toLowerCase();
            const matchLowStock = !filterLowStock || ((Number(item.stockActual) || 0) <= (Number(item.stockMinimo) || 0));
            return matchCategory && matchEmpresa && matchLowStock;
        });
    }, [data, selectedCategory, selectedEmpresa, filterLowStock]);

    // Columnas para la tabla
    const columns = useMemo(() => [
        { header: 'ID', accessorKey: 'id', size: 60 },
        { header: 'Nombre', accessorKey: 'nombre' },
        { header: 'Categoría', accessorKey: 'categoria' },
        {
            header: 'Empresa',
            accessorKey: 'empresa',
            cell: ({ row }) => (
                <span className="text-xs font-semibold px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                    {row.original.empresa || row.original.Empresa || 'General'}
                </span>
            )
        },
        {
            header: 'Stock',
            accessorKey: 'stockActual',
            cell: ({ row }) => (
                <div className="flex items-center">
                    <span className={`font-bold ${row.original.stockActual <= row.original.stockMinimo ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>
                        {row.original.stockActual}
                    </span>
                    <span className="text-xs text-gray-500 ml-1">
                        (Min: {row.original.stockMinimo})
                    </span>
                </div>
            )
        },
        { header: 'Ubicación', accessorKey: 'ubicacion' },
        {
            id: 'actions',
            header: 'Acciones',
            cell: ({ row }) => (
                <div className="flex space-x-2">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedConsumible(row.original);
                            setShowAssignModal(true);
                        }}
                        disabled={row.original.stockActual === 0}
                        className={`text-xs px-2 py-1 rounded border ${row.original.stockActual === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100'}`}
                        title="Entregar a usuario"
                    >
                        Entregar
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedConsumible(row.original);
                            setShowDetailModal(true);
                        }}
                        className="text-gray-500 hover:text-blue-600 p-1"
                        title="Ver historial"
                    >
                        <History className="h-4 w-4" />
                    </button>
                    <button
                        onClick={(e) => handleDelete(e, row.original)}
                        className="text-gray-500 hover:text-red-600 p-1"
                        title="Eliminar insumo"
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            )
        }
    ], [handleDelete]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleRowClick = (row) => {
        setSelectedConsumible(row);
        setShowDetailModal(true);
    };

    return (
        <div className={`space-y-6 ${embedded ? '' : 'p-6'}`}>
            <div className="flex justify-between items-center">
                <div>
                    {!embedded && (
                        <>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                                <Package className="mr-2 h-8 w-8 text-blue-600" />
                                Gestión de Insumos & Consumibles
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400">
                                Control de stock para periféricos, papelería, toners y repuestos.
                            </p>
                        </>
                    )}
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center text-sm font-medium shadow-sm transition-all"
                >
                    <Plus className="h-5 w-5 mr-1" />
                    Nuevo Insumo
                </button>
            </div>

            {/* ================= CARDS DE ESTADÍSTICAS Y FILTROS INTERACTIVOS ================= */}
            <div className="space-y-4">
                {/* 1. Resumen Global y Alertas */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    {/* Total Insumos */}
                    <div
                        onClick={() => {
                            setSelectedCategory('all');
                            setSelectedEmpresa('all');
                            setFilterLowStock(false);
                        }}
                        className={`bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all hover:scale-102 border ${
                            selectedCategory === 'all' && selectedEmpresa === 'all' && !filterLowStock ? 'ring-2 ring-blue-500 border-transparent' : 'border-blue-100 dark:border-blue-900/40'
                        }`}
                    >
                        <div className="p-3 rounded-lg text-blue-600 bg-white/70 dark:bg-black/20 flex-shrink-0">
                            <Layers className="h-6 w-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Insumos</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalTipos} <span className="text-xs font-normal text-gray-500">({stats.totalUnidades} u.)</span></p>
                        </div>
                    </div>

                    {/* Stock Bajo / Crítico */}
                    <div
                        onClick={() => setFilterLowStock(prev => !prev)}
                        className={`bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all hover:scale-102 border ${
                            filterLowStock ? 'ring-2 ring-amber-500 border-transparent' : 'border-amber-100 dark:border-amber-900/40'
                        }`}
                    >
                        <div className="p-3 rounded-lg text-amber-600 bg-white/70 dark:bg-black/20 flex-shrink-0">
                            <AlertTriangle className="h-6 w-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Stock Bajo / Crítico</p>
                            <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">{stats.stockBajo}</p>
                        </div>
                    </div>

                    {/* Empresas registradas */}
                    {stats.empresas.slice(0, 2).map((emp, idx) => (
                        <div
                            key={idx}
                            onClick={() => setSelectedEmpresa(selectedEmpresa === emp.nombre ? 'all' : emp.nombre)}
                            className={`bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all hover:scale-102 border ${
                                selectedEmpresa === emp.nombre ? 'ring-2 ring-indigo-500 border-transparent' : 'border-indigo-100 dark:border-indigo-900/40'
                            }`}
                        >
                            <div className="p-3 rounded-lg text-indigo-600 bg-white/70 dark:bg-black/20 flex-shrink-0">
                                <Building2 className="h-6 w-6" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider truncate">Empresa: {emp.nombre}</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{emp.stock} <span className="text-xs font-normal text-gray-500">u.</span></p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* 2. Filtros por Categoría */}
                <div className="flex flex-wrap items-center gap-2 pt-2">
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider mr-1">Categorías:</span>
                    <button
                        onClick={() => setSelectedCategory('all')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            selectedCategory === 'all'
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                        }`}
                    >
                        Todas ({stats.totalTipos})
                    </button>
                    {stats.categorias.map((cat, idx) => (
                        <button
                            key={idx}
                            onClick={() => setSelectedCategory(selectedCategory === cat.nombre ? 'all' : cat.nombre)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                                selectedCategory === cat.nombre
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                            }`}
                        >
                            <span>{cat.nombre}</span>
                            <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${selectedCategory === cat.nombre ? 'bg-blue-800 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}>
                                {cat.stock} u.
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {/* ================= TABLA DE DATOS ================= */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <DataTable
                    data={filteredData}
                    columns={columns}
                    isLoading={loading}
                    searchPlaceholder="Buscar insumos por nombre o ubicación..."
                    onRowClick={handleRowClick}
                />
            </div>

            {/* Modales */}
            <CrearConsumibleModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreated={fetchData}
            />

            <AsignarConsumibleModal
                isOpen={showAssignModal}
                onClose={() => setShowAssignModal(false)}
                consumible={selectedConsumible}
                onAsignado={fetchData}
            />

            <DetalleConsumibleModal
                isOpen={showDetailModal}
                onClose={() => setShowDetailModal(false)}
                consumible={selectedConsumible}
            />
        </div>
    );
}
